import React, { useState } from "react";
import { BookOpen, Plus, Trash2 } from "lucide-react";
import { Input, Textarea, Button, StatusBadge, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";
import { Field, StepShell } from "./_ui";
import type { StepProps } from "./types";

const SUGGESTIONS = [
  { question: "Do you offer emergency service?", answer: "" },
  { question: "What areas do you serve?", answer: "" },
  { question: "Do you offer free estimates?", answer: "" },
];

export function StepFaqs({ state, reload, goNext, goBack }: StepProps) {
  const [form, setForm] = useState({ question: "", answer: "" });
  const [adding, setAdding] = useState(false);
  const faqs = state.faqs ?? [];
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const add = async () => {
    if (form.question.trim().length < 3 || form.answer.trim().length < 1) {
      toast.error("Add both a question and an answer."); return;
    }
    setAdding(true);
    try {
      await api.addFaq({ question: form.question.trim(), answer: form.answer.trim(), category: "General" });
      await reload();
      setForm({ question: "", answer: "" });
      toast.success("Added to knowledge base");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id: string) => {
    try { await api.deleteFaq(id); await reload(); toast.success("Removed"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <StepShell
      icon={BookOpen}
      title="Answers your AI can trust"
      description="Add common questions and approved answers. Your AI only speaks from what you approve here."
      onBack={goBack}
      onContinue={goNext}
      continueLabel={faqs.length ? "Save & continue" : "Skip for now"}
    >
      {faqs.length > 0 && (
        <div className="space-y-2" data-testid="faqs-list">
          {faqs.map((f) => (
            <div key={f.id as string} className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3"
              data-testid={`faq-item-${f.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900">{(f.title as string) ?? (f.question as string)}</p>
                <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{(f.content as string) ?? (f.answer as string)}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => remove(f.id as string)} data-testid={`faq-delete-${f.id}`}>
                <Trash2 className="h-4 w-4 text-slate-400" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {faqs.length === 0 && (
        <div className="flex flex-wrap gap-2" data-testid="faq-suggestions">
          {SUGGESTIONS.map((s) => (
            <button key={s.question} type="button" onClick={() => set("question", s.question)}
              className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600 transition-colors hover:border-blue-300 hover:text-blue-700">
              {s.question}
            </button>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-dashed border-slate-300 p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-700">
          <Plus className="h-4 w-4" /> Add a question
        </div>
        <div className="space-y-4">
          <Field label="Question" testid="field-faq-q">
            <Input value={form.question} onChange={(e) => set("question", e.target.value)}
              placeholder="e.g. Do you offer emergency service?" data-testid="faq-question" />
          </Field>
          <Field label="Approved answer" testid="field-faq-a">
            <Textarea value={form.answer} onChange={(e) => set("answer", e.target.value)}
              placeholder="Yes — we offer 24/7 emergency service." data-testid="faq-answer" />
          </Field>
          <Button variant="outline" onClick={add} disabled={adding} data-testid="faq-add-btn">
            {adding ? "Adding…" : "Add to knowledge base"}
          </Button>
        </div>
      </div>
      {faqs.length === 0 && (
        <StatusBadge tone="amber" data-testid="faqs-hint">Add at least one answer so your AI can respond confidently</StatusBadge>
      )}
    </StepShell>
  );
}
